// Thin fetch wrapper plus TanStack Query helpers used by every page.
//
// In dev, Vite proxies /api and /auth to FastAPI on :8000 (see vite.config).
// In prod, FastAPI serves the SPA so same-origin works out of the box.
//
// In the standalone landing build (separate origin), VITE_API_BASE is set so
// relative paths like "/auth/me" are rewritten to absolute URLs targeting the
// app subdomain. Cross-origin cookies still ride because both hosts share the
// same registrable domain (see backend SESSION_COOKIE_DOMAIN).
//
// Always sends credentials so the session cookie rides along.

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";

const API_BASE = (import.meta.env.VITE_API_BASE ?? "")
  .trim()
  .replace(/\/$/, "");

function resolveUrl(path: string): string {
  if (!API_BASE) return path;
  if (/^[a-z][a-z0-9+.-]*:/i.test(path)) return path;
  return API_BASE + (path.startsWith("/") ? path : `/${path}`);
}

export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export async function apiFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(resolveUrl(path), {
    ...init,
    headers,
    credentials: "include",
  });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.clone().json();
    } catch {
      try {
        body = await res.clone().text();
      } catch {
        body = null;
      }
    }
    throw new ApiError(res.status, `${res.status} ${res.statusText}`, body);
  }
  return res;
}

export async function apiJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(path, init);
  // 204 No Content
  if (res.status === 204) return undefined as unknown as T;
  return (await res.json()) as T;
}

/** Convenience hook around apiJson. */
export function useApi<T>(
  path: string,
  opts?: Omit<UseQueryOptions<T, ApiError>, "queryKey" | "queryFn">,
) {
  return useQuery<T, ApiError>({
    queryKey: [path],
    queryFn: () => apiJson<T>(path),
    ...opts,
  });
}

export type HttpMethod = "POST" | "PUT" | "PATCH" | "DELETE";

/** Convenience helper for one-off mutations against a known path. */
export function useApiMutation<TBody, TResponse>(
  method: HttpMethod,
  pathOrBuilder: string | ((vars: TBody) => string),
  opts?: { invalidate?: string[] },
) {
  const qc = useQueryClient();
  return useMutation<TResponse, ApiError, TBody>({
    mutationFn: async (vars: TBody) => {
      const path =
        typeof pathOrBuilder === "function"
          ? pathOrBuilder(vars)
          : pathOrBuilder;
      const init: RequestInit = { method };
      if (vars !== undefined && method !== "DELETE") {
        init.body = JSON.stringify(vars);
      }
      return apiJson<TResponse>(path, init);
    },
    onSuccess: () => {
      if (opts?.invalidate) {
        for (const key of opts.invalidate) {
          qc.invalidateQueries({ queryKey: [key] });
        }
      }
    },
  });
}

/**
 * Plain (non-hook) mutation. Useful from outside React (e.g. autosave).
 */
export async function apiMutation<T>(
  method: HttpMethod | "GET",
  path: string,
  body?: unknown,
): Promise<T> {
  const init: RequestInit = { method };
  if (body !== undefined) init.body = JSON.stringify(body);
  return apiJson<T>(path, init);
}
